// Mobile viewport helpers: step scroll reset and keeping a focused address field visible above the iOS keyboard.
const inFrame = () => window.parent !== window;
const headerH = () => {
  const h = document.querySelector("[data-sticky-header]") as HTMLElement | null;
  return h && h.offsetParent !== null && getComputedStyle(h).position === "sticky" ? h.offsetHeight : 0;
};

// Shows the start of a new step after it renders. Instant (no smooth scroll), only scrolls up, and tells an embedding parent page.
export function resetScroll(el: HTMLElement | null) {
  const ae = document.activeElement as HTMLElement | null;
  if (ae && ae !== document.body && ae.blur) ae.blur();
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const target = Math.max(0, el ? el.getBoundingClientRect().top + window.scrollY - headerH() - 8 : 0);
    if (window.scrollY > target) window.scrollTo(0, target);
    if (inFrame()) {
      window.parent.postMessage({ type: "iqw:step" }, "*");
      setTimeout(() => window.parent.postMessage({ type: "iqw:step" }, "*"), 350); // again after the frame resizes
    }
  }));
}

// Once per focus: move the field near the top of the visible area and cap the suggestion list to the space left above the keyboard.
export function keepFieldVisible(el: HTMLElement): () => void {
  const vv = window.visualViewport;
  let timer: any;
  const fit = () => {
    const bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    el.style.setProperty("--iqw-list-max", `${Math.max(150, Math.round(bottom - el.getBoundingClientRect().bottom - 12))}px`);
  };
  const place = () => {
    const r = el.getBoundingClientRect();
    // On narrow screens Google shows a full-screen overlay pinned to the top of the (i)frame; the host collapses.
    const overlay = r.height < 10;
    if (inFrame()) window.parent.postMessage({ type: "iqw:field", offset: overlay ? 0 : Math.round(r.top + window.scrollY) }, "*");
    if (overlay) return;
    const delta = r.top - (vv ? vv.offsetTop : 0) - headerH() - 12;
    if (Math.abs(delta) > 4) window.scrollBy(0, delta);
    fit();
  };
  const touch = () => window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 640;
  const onFocus = () => {
    if (!touch()) return;
    if (!inFrame()) document.documentElement.classList.add("iqw-kb-open");
    clearTimeout(timer); timer = setTimeout(place, el.getBoundingClientRect().height < 10 ? 0 : 350);
  };
  const onBlur = () => { clearTimeout(timer); setTimeout(() => { if (!el.matches(":focus-within")) document.documentElement.classList.remove("iqw-kb-open"); }, 150); };
  el.addEventListener("focusin", onFocus);
  el.addEventListener("focusout", onBlur);
  vv?.addEventListener("resize", fit);
  return () => {
    clearTimeout(timer);
    el.removeEventListener("focusin", onFocus);
    el.removeEventListener("focusout", onBlur);
    vv?.removeEventListener("resize", fit);
    document.documentElement.classList.remove("iqw-kb-open");
  };
}

export const closeSuggestions = (el: HTMLElement) => {
  const ae = document.activeElement as HTMLElement | null;
  if (ae && (ae === el || el.contains(ae))) ae.blur();
  (el as any).blur?.();
  document.documentElement.classList.remove("iqw-kb-open");
};
